/**
 * Payer Connections — how long a selected enrollment has sat in its current
 * status (GDN-3684).
 *
 * Anchored on Stedi's `statusLastUpdatedAt` (`status_updated_at`), which only
 * moves on a status transition — never on `created_at` (the roster's
 * days-blocked anchor) or `updated_at` (any edit). Pure: `asOf` is always an
 * explicit parameter, never `Date.now()`.
 */

import type { PayerState } from './types';

export const STATUS_DATE_UNAVAILABLE = 'Status date unavailable';

export interface StatusAge {
  /** Short local date of the last status change, e.g. "Sep 3". */
  since: string;
  /** Whole days in the current status, never negative. */
  days: number;
  /** "today", "1 day", "7 days". */
  elapsed: string;
  /** Exact timestamp with an explicit zone, e.g. "Sep 3, 2026, 9:00 AM EDT". */
  exact: string;
}

export interface StatusAgeOptions {
  locales?: string | string[];
  /** Pin the zone (tests, stories); defaults to the viewer's. */
  timeZone?: string;
}

const DAY_MS = 86_400_000;

function formatElapsed(days: number): string {
  if (days === 0) return 'today';
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * Null when the timestamp is missing or unparseable — the caller renders
 * `STATUS_DATE_UNAVAILABLE` rather than substituting another date. A
 * timestamp after `asOf` (clock skew) clamps to zero days instead of going
 * negative.
 */
export function statusAge(
  statusUpdatedAt: string | null | undefined,
  asOf: Date,
  { locales, timeZone }: StatusAgeOptions = {},
): StatusAge | null {
  if (!statusUpdatedAt) return null;
  const at = new Date(statusUpdatedAt);
  if (Number.isNaN(at.getTime()) || Number.isNaN(asOf.getTime())) return null;

  const days = Math.max(Math.floor((asOf.getTime() - at.getTime()) / DAY_MS), 0);
  const sameYear =
    at.toLocaleDateString(locales, { year: 'numeric', timeZone }) ===
    asOf.toLocaleDateString(locales, { year: 'numeric', timeZone });

  return {
    since: at.toLocaleDateString(locales, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
      timeZone,
    }),
    days,
    elapsed: formatElapsed(days),
    exact: at.toLocaleString(locales, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone,
    }),
  };
}

/** Urgency of time spent in a status that is still waiting on someone. */
export type StatusAgeSeverity = 'success' | 'warning' | 'error';

const WARNING_AFTER_DAYS = 14;
const ERROR_AFTER_DAYS = 30;

/** Under two weeks is fine, two weeks is getting old, a month is stuck. */
export function statusAgeSeverity(days: number): StatusAgeSeverity {
  if (days >= ERROR_AFTER_DAYS) return 'error';
  if (days >= WARNING_AFTER_DAYS) return 'warning';
  return 'success';
}

/** Terminal or done states are not stuck, so their age carries no urgency. */
export function isAgeActionable(state: PayerState): boolean {
  return state !== 'canceled' && state !== 'ready' && state !== 'live';
}
